import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, LoadingSpinner } from '@tadagram/ui';
import { apiService } from '../../services/api';
import { ExternalLink, Trash2, RefreshCw, TrendingUp, ChevronLeft, ChevronRight, Download, ThumbsUp, MessageSquare, Share2, Users, X } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EngagementProfile {
  name: string;
  profile_url: string;
}

interface EngagementPostLead {
  id: string;
  business_id: string;
  label: string;
  keyword: string;
  source_url: string;
  post_url: string;
  author: string;
  author_url?: string;
  text: string;
  post_date?: string;
  likes_count: number;
  comments_count: number;
  shares_count: number;
  total_engagement: number;
  reactors?: EngagementProfile[];
  commenters?: EngagementProfile[];
  collected_at: string;
}

interface LeadsResponse {
  leads: EngagementPostLead[];
  total: number;
  page: number;
  limit: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatSourceLabel(url: string): string {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    if (parts[0] === 'groups' && parts[1]) return `groups/${parts[1]}`;
    if (parts[0]) return parts[0];
    return u.hostname;
  } catch {
    return url;
  }
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function truncate(text: string, max = 180): string {
  if (!text) return '—';
  return text.length > max ? text.slice(0, max) + '…' : text;
}

function shortLabel(label: string, max = 22): string {
  if (!label) return 'default';
  return label.length > max ? label.slice(0, max) + '…' : label;
}

function fmtNum(n: number): string {
  if (!n) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1).replace('.0', '') + 'k';
  return String(n);
}

// ─── Component ────────────────────────────────────────────────────────────────

// ─── Detail Modal ─────────────────────────────────────────────────────────────

interface DetailModalProps {
  lead: EngagementPostLead;
  onClose: () => void;
}

const DetailModal: React.FC<DetailModalProps> = ({ lead, onClose }) => {
  const { t } = useTranslation('leads');
  const reactors = lead.reactors ?? [];
  const commenters = lead.commenters ?? [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative z-10 bg-bg-primary border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-text-primary line-clamp-1">{lead.author || '—'}</h3>
            <p className="text-xs text-text-tertiary mt-0.5 line-clamp-1">{lead.post_url}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded text-text-tertiary hover:text-text-primary hover:bg-bg-secondary transition-colors shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Reactors */}
          <section>
            <h4 className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
              <ThumbsUp size={12} />
              {t('reactors', { count: reactors.length })}
            </h4>
            {reactors.length === 0 ? (
              <p className="text-xs text-text-tertiary italic">{t('noProfileData')}</p>
            ) : (
              <ul className="space-y-1">
                {reactors.map((r, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <a
                      href={r.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-tg-primary hover:underline"
                    >
                      {r.name || r.profile_url}
                      <ExternalLink size={10} className="opacity-60 shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Commenters */}
          <section>
            <h4 className="flex items-center gap-1.5 text-xs font-semibold text-violet-600 uppercase tracking-wide mb-2">
              <MessageSquare size={12} />
              {t('commenters', { count: commenters.length })}
            </h4>
            {commenters.length === 0 ? (
              <p className="text-xs text-text-tertiary italic">{t('noProfileData')}</p>
            ) : (
              <ul className="space-y-1">
                {commenters.map((c, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <a
                      href={c.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-tg-primary hover:underline"
                    >
                      {c.name || c.profile_url}
                      <ExternalLink size={10} className="opacity-60 shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export const EngagementPostLeadsDashboard: React.FC = () => {
  const { t } = useTranslation('leads');
  const [leads, setLeads] = useState<EngagementPostLead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [labels, setLabels] = useState<string[]>([]);
  const [filterLabel, setFilterLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [detailLead, setDetailLead] = useState<EngagementPostLead | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // ── Fetch leads ──────────────────────────────────────────────────────────
  const fetchLeads = useCallback(async (targetPage = 1, label = filterLabel) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        page: String(targetPage),
        limit: String(limit),
      };
      if (label) params.label = label;

      const qs = new URLSearchParams(params).toString();
      const res = await apiService.get<{ success: boolean; data: LeadsResponse }>(
        `/api/v1/engagement-post-leads?${qs}`,
      );
      const data: LeadsResponse = res?.data ?? { leads: [], total: 0, page: 1, limit };
      setLeads(data.leads ?? []);
      setTotal(data.total ?? 0);
      setPage(data.page ?? 1);

      if (!label) {
        const seen = new Set<string>();
        (data.leads ?? []).forEach((l) => { if (l.label) seen.add(l.label); });
        setLabels((prev) => Array.from(new Set([...prev, ...seen])).sort());
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filterLabel, limit]);

  useEffect(() => {
    fetchLeads(1, filterLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLabelChange = (label: string) => {
    setFilterLabel(label);
    fetchLeads(1, label);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchLeads(newPage, filterLabel);
  };

  const handleDelete = async (lead: EngagementPostLead) => {
    if (!window.confirm(t('deletePostConfirm', { author: lead.author, url: lead.post_url }))) return;
    setDeletingId(lead.id);
    try {
      await apiService.delete(`/api/v1/engagement-post-leads/${lead.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err: any) {
      alert(t('deleteFailed', { error: err?.message ?? '' }));
    } finally {
      setDeletingId(null);
    }
  };

  // ── Export to CSV (Excel-compatible) ───────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const params: Record<string, string> = { page: '1', limit: '10000' };
      if (filterLabel) params.label = filterLabel;
      const qs = new URLSearchParams(params).toString();
      const res = await apiService.get<{ success: boolean; data: LeadsResponse }>(
        `/api/v1/engagement-post-leads?${qs}`,
      );
      const allLeads = res?.data?.leads ?? [];
      if (allLeads.length === 0) {
        alert(t('noDataToExport'));
        return;
      }

      const escapeCSV = (val: string | number) => {
        const s = String(val ?? '');
        const escaped = s.replace(/"/g, '""');
        return /[,"\n\r]/.test(s) ? `"${escaped}"` : escaped;
      };

      const headers = ['Label', 'Nguồn', 'Tác giả', 'Nội dung', 'Link bài viết', '👍 Likes', '💬 Comments', '🔁 Shares', '📊 Tổng tương tác', 'Ngày đăng', 'Ngày thu thập'];
      const rows = allLeads.map((l) => [
        escapeCSV(l.label || ''),
        escapeCSV(l.source_url || ''),
        escapeCSV(l.author || ''),
        escapeCSV(l.text || ''),
        escapeCSV(l.post_url || ''),
        escapeCSV(l.likes_count ?? 0),
        escapeCSV(l.comments_count ?? 0),
        escapeCSV(l.shares_count ?? 0),
        escapeCSV(l.total_engagement ?? 0),
        escapeCSV(l.post_date || ''),
        escapeCSV(formatDate(l.collected_at)),
      ]);

      const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `engagement_leads_${filterLabel || 'all'}_${dateStr}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(t('exportFailed', { error: err?.message ?? '' }));
    } finally {
      setExporting(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/10 text-emerald-500 shrink-0">
            <TrendingUp size={18} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-text-primary">{t('engagementTitle')}</h2>
            <p className="text-xs text-text-secondary mt-0.5">
              {t('engagementSubtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleExport}
            disabled={exporting || loading || leads.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            title="Xuất dữ liệu ra file Excel (CSV)"
          >
            <Download size={14} className={exporting ? 'animate-bounce' : ''} />
            {exporting ? t('exporting') : t('exportExcel')}
          </button>
          <button
            onClick={() => fetchLeads(1, filterLabel)}
            disabled={loading}
            className="p-2 rounded-lg text-text-secondary hover:bg-bg-secondary hover:text-text-primary transition-colors disabled:opacity-40 shrink-0"
            title={t('refresh')}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs text-text-secondary whitespace-nowrap font-medium">{t('labelFilter')}</label>
        <select
          className="input text-sm py-1.5 w-auto min-w-[180px] max-w-xs"
          value={filterLabel}
          onChange={(e) => handleLabelChange(e.target.value)}
        >
          <option value="">{t('all')}</option>
          {labels.map((lb) => (
            <option key={lb} value={lb}>{lb}</option>
          ))}
        </select>
        <span className="text-xs text-text-tertiary ml-auto">
          {t('totalPosts', { count: total })}
        </span>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-tg-danger/10 border border-tg-danger/30 text-tg-danger rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="md" text={t('loading')} />
        </div>
      ) : leads.length === 0 ? (
        <Card className="p-10 text-center">
          <TrendingUp size={40} className="mx-auto mb-3 text-text-tertiary opacity-40" />
          <p className="text-sm text-text-secondary">{t('noData')}</p>
          <p className="text-xs text-text-tertiary mt-1">{t('workerNote')}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-bg-secondary">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-24">{t('colLabel')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-32">{t('colSource')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-32">{t('colAuthor')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide">{t('colContent')}</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-20">
                    <ThumbsUp size={12} className="inline mr-0.5" />Likes
                  </th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-20">
                    <MessageSquare size={12} className="inline mr-0.5" />Cmt
                  </th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-20">
                    <Share2 size={12} className="inline mr-0.5" />Share
                  </th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-emerald-600 uppercase tracking-wide w-20">
                    <TrendingUp size={12} className="inline mr-0.5" />Total
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-10 text-center">{t('colLink')}</th>
                  <th className="px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-10 text-center">{t('colDetail')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-24">{t('colPostDate')}</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-bg-secondary transition-colors duration-100">
                    {/* Label */}
                    <td className="px-4 py-3">
                      <span
                        className="badge badge-success inline-block max-w-[90px] truncate align-middle"
                        title={lead.label || 'default'}
                      >
                        {shortLabel(lead.label)}
                      </span>
                    </td>

                    {/* Source */}
                    <td className="px-4 py-3">
                      <a
                        href={lead.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-tg-primary hover:underline max-w-[120px] truncate"
                        title={lead.source_url}
                      >
                        <span className="truncate">{formatSourceLabel(lead.source_url)}</span>
                        <ExternalLink size={10} className="shrink-0 opacity-60" />
                      </a>
                    </td>

                    {/* Author */}
                    <td className="px-4 py-3">
                      {lead.author_url ? (
                        <a
                          href={lead.author_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-text-primary hover:text-tg-primary max-w-[120px] truncate"
                          title={lead.author}
                        >
                          <span className="truncate">{lead.author || '—'}</span>
                          <ExternalLink size={10} className="shrink-0 opacity-50" />
                        </a>
                      ) : (
                        <span className="text-xs font-medium text-text-primary max-w-[120px] truncate block" title={lead.author}>
                          {lead.author || '—'}
                        </span>
                      )}
                    </td>

                    {/* Content */}
                    <td className="px-4 py-3">
                      <p className="text-xs text-text-primary leading-relaxed line-clamp-3" title={lead.text}>
                        {truncate(lead.text)}
                      </p>
                    </td>

                    {/* Likes */}
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs font-medium text-blue-600">
                        {fmtNum(lead.likes_count)}
                      </span>
                    </td>

                    {/* Comments */}
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs font-medium text-violet-600">
                        {fmtNum(lead.comments_count)}
                      </span>
                    </td>

                    {/* Shares */}
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs font-medium text-amber-600">
                        {fmtNum(lead.shares_count)}
                      </span>
                    </td>

                    {/* Total engagement */}
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                        {fmtNum(lead.total_engagement)}
                      </span>
                    </td>

                    {/* Post link */}
                    <td className="px-4 py-3 text-center">
                      {lead.post_url ? (
                        <a
                          href={lead.post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-tg-primary/10 text-tg-primary hover:bg-tg-primary/20 transition-colors"
                          title={lead.post_url}
                        >
                          <ExternalLink size={13} />
                        </a>
                      ) : (
                        <span className="text-text-tertiary text-xs">—</span>
                      )}
                    </td>

                    {/* Detail */}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setDetailLead(lead)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors"
                        title={`Xem ${(lead.reactors?.length ?? 0) + (lead.commenters?.length ?? 0)} người tương tác`}
                      >
                        <Users size={13} />
                      </button>
                    </td>

                    {/* Post date */}
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-text-secondary">
                      {lead.post_date || '—'}
                    </td>

                    {/* Delete */}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDelete(lead)}
                        disabled={deletingId === lead.id}
                        className="p-1.5 rounded text-text-tertiary hover:text-tg-danger hover:bg-tg-danger/10 transition-colors disabled:opacity-40"
                        title="Xóa"
                      >
                        {deletingId === lead.id ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail Modal */}
      {detailLead && <DetailModal lead={detailLead} onClose={() => setDetailLead(null)} />}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-1">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1 || loading}
            className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs text-text-secondary">
            Trang {page} / {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages || loading}
            className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};
